<template>
  <div class="flex flex-col min-h-screen bg-slate-50">
    <Header />
    <FlashMessage v-if="messages.text !== ''" :message="messages" />

    <div class="flex-1 py-7">
      <div class="mx-auto max-w-[1200px] px-4 sm:px-6">
        <!-- Page Header -->
       <div
  class="flex flex-col-reverse gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between"
>
  <div>
    <h1 class="text-2xl font-extrabold text-gray-800 tracking-tight">
      <span class="text-blue-600 capitalize">{{ surveyTitle }}</span> Responses
      <span class="ml-2 text-sm font-normal text-gray-500">
        ({{ responseCount || 0 }})
      </span>
    </h1>

    <p class="text-sm text-gray-500 mt-0.5">
      View student responses for this survey
    </p>
  </div>

  <router-link
    :to="{ name: 'SurveyList' }"
    class="inline-flex self-end items-center justify-center rounded-xl bg-blues px-5 py-1.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-600 sm:self-auto"
  >
    Back
  </router-link>
</div>

        <!-- Main Card -->
        <div
          class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-4"
        >
          <!-- Filters Row -->
          <div class="flex flex-col sm:flex-row gap-3 items-center mb-4">
            <!-- Search -->
            <div class="relative flex-1 max-w-sm">
              <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                v-model="searchQuery"
                type="text"
                @input="fetchResponses(1)"
                placeholder="Search responses..."
                class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <!-- Clear -->
            <button
              @click="clearFilters"
              class="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
              title="Clear Filters"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Table -->
          <div class="overflow-x-auto border rounded-lg">
            <table class="min-w-full divide-y divide-gray-100">
              <thead class="bg-gray-100">
                <tr class="text-left text-xs font-semibold text-blues">
                  <th class="px-4 py-3 border-b border-r w-10">S.No</th>
                  <th class="px-4 py-3 border-b border-r max-w-sm">
                    User Details
                  </th>
                  <th class="px-4 py-3 border-b border-r">PRN Number</th>
                  <th class="px-4 py-3 border-b border-r">Program & Batch</th>
                  <th class="px-4 py-3 border-b border-r">Semester</th>
                  <th class="px-4 py-3 border-b border-r text-center w-28">
                    Progress
                  </th>
                  <th class="px-4 py-3 border-b w-32">Submitted At</th>
                </tr>
              </thead>

              <!-- Loading Skeleton -->
              <tbody v-if="loading" class="divide-y divide-gray-50">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="px-4 py-3">
                    <div class="h-3 w-6 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-3">
                      <div
                        class="h-8 w-8 bg-gray-100 rounded-full shrink-0"
                      ></div>
                      <div class="flex flex-col gap-1.5">
                        <div class="h-3 w-32 bg-gray-100 rounded"></div>
                        <div class="h-2 w-24 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-24 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-32 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-32 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-12 mx-auto bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-20 bg-gray-100 rounded"></div>
                  </td>
                </tr>
              </tbody>

              <!-- Data -->
              <tbody
                v-else-if="responses.length > 0"
                class="divide-y divide-gray-100 text-xs"
              >
                <tr
                  v-for="(resp, index) in responses"
                  :key="resp.id"
                  class="hover:bg-slate-50 transition-colors cursor-pointer"
                  @click="openSidebar(resp)"
                >
                  <td class="px-4 py-3 border-r text-center text-gray-500">
                    {{ (currentPage - 1) * pageSize + index + 1 }}
                  </td>
                  <td class="px-4 py-3 border-r">
                    <div class="flex items-center gap-3">
                      <div
                        class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold shrink-0 uppercase"
                      >
                        {{ (resp.display_name || resp.email || "?").charAt(0) }}
                      </div>
                      <div class="flex flex-col">
                        <p
                          class="font-semibold text-slate-800 line-clamp-1"
                          :title="resp.display_name"
                        >
                          {{ resp.display_name || "Student" }}
                        </p>
                        <p
                          class="text-xs text-slate-500 line-clamp-1 mt-0.5"
                          :title="resp.email"
                        >
                          {{ resp.email || "N/A" }}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3 border-r text-slate-700 font-medium">
                    {{ resp.prn_number || "N/A" }}
                  </td>
                  <td class="px-4 py-3 border-r text-slate-600">
                    <span v-if="resp.program" class="font-semibold"
                      >{{ resp.program }}
                    </span>
                    <span v-if="resp.batch" class="text-xs text-gray-400"
                      >({{ resp.batch }})</span
                    >
                    <span v-if="!resp.program && !resp.batch">N/A</span>
                  </td>
                  <td
                    class="px-4 py-3 border-r text-slate-600"
                    :title="resp.semester"
                  >
                    {{ resp.semester || "N/A" }}
                  </td>
                  <td
                    class="px-4 py-3 border-r text-center font-medium font-mono text-sm"
                  >
                    <span class="text-emerald-600">{{
                      resp.answered_count || 0
                    }}</span
                    ><span class="text-slate-300 mx-1">/</span
                    ><span class="text-slate-500">{{
                      resp.questions_count || 0
                    }}</span>
                  </td>
                  <td class="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {{ formatDate(resp.submitted_at || resp.updated_at) }}
                  </td>
                </tr>
              </tbody>

              <!-- Empty State -->
              <tbody v-else>
                <tr>
                  <td colspan="7" class="py-16 text-center">
                    <div class="flex flex-col items-center gap-3">
                      <div
                        class="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center"
                      >
                        <svg
                          class="w-8 h-8 text-slate-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="1.5"
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                      </div>
                      <p class="text-slate-500 font-semibold">
                        No responses found
                      </p>
                      <p class="text-slate-400 text-xs">
                        This survey has no submitted responses yet.
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div
            v-if="responseCount > pageSize || totalPages > 1"
            class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative"
          >
            <div
              class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal"
            >
              <span class="flex md:hidden">Rows per page:</span>
              <select
                v-model="pageSize"
                @change="onPageSizeChange"
                class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
            </div>
            <div class="flex justify-center">
              <Pagination
                :totalPages="totalPages"
                ref="paginationRef"
                @page-changed="fetchResponses"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer />

    <!-- Right Sidebar JSON Viewer -->
    <transition
      enter-active-class="transition-opacity ease-linear duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity ease-linear duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <!-- Backdrop -->
      <div
        v-if="sidebarOpen"
        class="fixed inset-0 bg-slate-900 bg-opacity-40 z-40"
        @click="closeSidebar"
      ></div>
    </transition>

    <transition
      enter-active-class="transform transition ease-in-out duration-300"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transform transition ease-in-out duration-300"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <!-- Panel -->
      <div v-if="sidebarOpen" class="fixed inset-y-0 right-0 flex max-w-full z-50">
        <div
          class="w-screen max-w-lg bg-white shadow-2xl flex flex-col h-full border-l border-gray-200"
        >
          <!-- Header -->
          <div
            class="px-6 py-4 border-b border-gray-100 flex items-start justify-between bg-white relative z-20 shadow-sm"
          >
            <div
              v-if="!selectedResponseDetails"
              class="flex items-center gap-2 mt-1"
            >
              <svg
                class="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h2 class="text-lg font-bold text-slate-800">
                Detailed Response
              </h2>
            </div>

            <div v-else class="flex items-center gap-3.5">
              <div
                class="h-11 w-11 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center text-blue-700 font-extrabold text-lg shrink-0 shadow-sm border border-blue-100"
              >
                {{
                  (
                    selectedResponseDetails.display_name ||
                    selectedResponseDetails.email ||
                    "?"
                  )
                    .charAt(0)
                    .toUpperCase()
                }}
              </div>
              <div class="flex flex-col">
                <h3
                  class="text-[17px] font-extrabold text-slate-800 leading-tight mb-0.5 capitalize"
                >
                  {{ selectedResponseDetails.display_name || "Student" }}
                </h3>
                <p class="text-[13px] font-medium text-blue-600 leading-tight">
                  {{ selectedResponseDetails.email }}
                </p>
                <div class="flex flex-col mt-1.5 gap-0.5">
                  <p
                    class="text-[11px]   font-bold text-slate-500"
                  >
                    Submitted:
                    {{
                      formatDate(
                        selectedResponseDetails.submitted_at ||
                          selectedResponseDetails.updated_at,
                      )
                    }}
                  </p>
                  <p
                    class="text-[11px]   font-bold text-slate-500"
                  >
                    Answers:
                    <span class="text-emerald-600 font-extrabold">{{
                      selectedResponseDetails.answered_count || 0
                    }}</span>
                    <span class="text-slate-300 mx-0.5">/</span>
                    {{ selectedResponseDetails.questions_count || 0 }}
                  </p>
                  
                </div>
              </div>
            </div>

            <button
              @click="closeSidebar"
              class="p-2 -mr-2 -mt-1 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
            >
              <svg
                class="w-5 h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div
            class="flex-1 overflow-y-auto bg-slate-50 custom-scrollbar relative"
          >
            <!-- Loading Indicator -->
            <div
              v-if="sidebarLoading"
              class="absolute inset-0 flex flex-col items-center justify-center bg-slate-50/80 z-10 backdrop-blur-sm"
            >
              <div
                class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"
              ></div>
              <p class="mt-4 text-slate-500 font-medium">Loading details...</p>
            </div>

            <div v-if="selectedResponseDetails" class="p-6 pb-24">
              <!-- Questions List -->
              <div class="flex items-center justify-between mb-4 px-1">
                <h4 class="text-slate-800 font-extrabold text-lg">
                  Survey Answers
                </h4>
                <button
                  @click="downloadResponseData"
                  :disabled="downloadLoading"
                  class="p-2 -mr-2 rounded-xl text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Export Response to Excel"
                >
                  <svg
                    v-if="!downloadLoading"
                    class="w-5 h-5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    ></path>
                  </svg>
                  <svg
                    v-else
                    class="w-5 h-5 animate-spin flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </button>
              </div>
              <div class="flex flex-col gap-5">
                <div
                  v-for="(q, idx) in selectedResponseDetails.questions"
                  :key="q.question_id"
                  class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
                >
                  <div
                    class="px-4 py-3 bg-slate-50/80 border-b border-slate-100 flex gap-3 sm:gap-4 items-start"
                  >
                    <div
                      class="bg-blue-600 text-white text-[11px] mt-0.5 font-bold w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                    >
                      {{ idx + 1 }}
                    </div>
                    <div>
                      <p
                        class="text-[13px] sm:text-sm font-semibold text-slate-800 leading-snug"
                      >
                        {{ q.question }}
                      </p>
                      <div v-if="q.question_type" class="mt-1.5">
                        <span class="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 font-extrabold text-[10px] capitalize tracking-wide rounded-full border border-blue-100">
                          {{ q.question_type.replace(/_/g, ' ') }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="p-4">
                    <div
                      v-if="!q.answered"
                      class="text-orange-500 bg-orange-50 rounded-xl p-3 font-medium text-[13px] sm:text-sm flex items-center gap-2"
                    >
                      <svg
                        class="w-4 h-4 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        ></path>
                      </svg>
                      This question was skipped.
                    </div>
                    <div v-else>
                      <span
                        class="text-[10px] sm:text-[11px] uppercase font-extrabold text-slate-400 tracking-wider mb-1.5 block px-1"
                        >Answer Given</span
                      >
                      <div class="flex flex-col gap-1.5">
                        <!-- Image Answer -->
                        <div
                          v-if="
                            typeof q.answer === 'object' &&
                            q.answer !== null &&
                            q.answer.image
                          "
                          class="mt-1 flex flex-col items-start gap-2"
                        >
                          <div class="group relative inline-block">
                            <img
                              :src="q.answer.image"
                              class="max-w-[220px] sm:max-w-xs h-auto rounded-xl border border-slate-200 shadow-sm max-h-40 object-contain bg-slate-50"
                              alt="Answer Image"
                            />
                            <a
                              :href="q.answer.image"
                              download
                              target="_blank"
                              class="absolute top-2 right-2 p-1.5 bg-white/90 text-slate-600 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-all border border-slate-100"
                              title="Download Image"
                            >
                              <svg
                                class="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                ></path>
                              </svg>
                            </a>
                          </div>
                          <div
                            v-if="q.answer.label"
                            class="text-[13px] sm:text-sm font-medium text-slate-700 bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm"
                          >
                            {{ q.answer.label }}
                          </div>
                        </div>

                        <!-- Object with Options Array -->
                        <template
                          v-else-if="
                            typeof q.answer === 'object' &&
                            q.answer !== null &&
                            Array.isArray(q.answer.options)
                          "
                        >
                          <div
                            v-if="q.answer.options.length === 0"
                            class="text-slate-500 italic text-[13px] px-1"
                          >
                            No options selected.
                          </div>
                          <div
                            v-for="(opt, oIdx) in q.answer.options"
                            :key="oIdx"
                            class="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-3.5 py-2.5 text-[13px] sm:text-sm font-medium flex items-center gap-2.5 shadow-sm"
                          >
                            <div
                              class="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"
                            ></div>
                            <span class="break-words leading-tight">{{
                              opt
                            }}</span>
                          </div>
                        </template>

                        <!-- Direct Array -->
                        <template v-else-if="Array.isArray(q.answer)">
                          <div
                            v-if="q.answer.length === 0"
                            class="text-slate-500 italic text-[13px] px-1"
                          >
                            No options selected.
                          </div>
                          <div
                            v-for="(opt, oIdx) in q.answer"
                            :key="oIdx"
                            class="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-3.5 py-2.5 text-[13px] sm:text-sm font-medium flex items-center gap-2.5 shadow-sm"
                          >
                            <div
                              class="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"
                            ></div>
                            <span class="break-words leading-tight">{{
                              opt
                            }}</span>
                          </div>
                        </template>

                        <!-- Primitive (String, Number, Boolean) -->
                        <div
                          v-else-if="
                            typeof q.answer !== 'object' || q.answer === null
                          "
                          class="rounded-xl px-3.5 py-2.5 text-[13px] sm:text-sm font-medium shadow-sm border bg-emerald-50 border-emerald-200 text-emerald-800"
                        >
                          <span
                            class="break-words leading-relaxed whitespace-pre-wrap"
                            >{{
                              q.answer !== null &&
                              q.answer !== undefined &&
                              q.answer !== ""
                                ? q.answer
                                : "No answer provided."
                            }}</span
                          >
                        </div>

                        <!-- Map/Grid Answers or other objects -->
                        <div
                          v-else
                          class="flex flex-col gap-2"
                        >
                          <template v-if="typeof q.answer === 'object' && q.answer !== null && Object.keys(q.answer).length > 0">
                            <div
                              v-for="(val, key) in q.answer"
                              :key="key"
                              class="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4"
                            >
                              <span class="text-[13px] sm:text-sm font-medium text-slate-700">{{ key.endsWith('"') ? key.slice(0, -1) : key }}</span>
                              <span class="text-[13px] sm:text-sm font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 self-start sm:self-auto break-all sm:break-normal">{{ typeof val === 'object' ? JSON.stringify(val) : val }}</span>
                            </div>
                          </template>
                          <div v-else class="text-slate-500 italic text-[13px] px-1">
                            No answer provided.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else-if="!sidebarLoading"
              class="p-8 flex flex-col items-center justify-center h-[70vh] gap-4 text-center"
            >
              <div class="bg-red-50 text-red-500 p-5 rounded-full mb-2">
                <svg
                  class="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <h3 class="font-bold text-slate-800 text-lg">
                Failed to load data
              </h3>
              <p class="text-slate-500 text-sm max-w-xs">
                We encountered an error while trying to fetch the survey
                response details. Please try again.
              </p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Header from "@/components/Admin/Header.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import Footer from "@/components/Footer.vue";
import Pagination from "@/components/Pagination.vue";
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "SurveyResponses",
  components: {
    Header,
    FlashMessage,
    Footer,
    Pagination,
  },
  data() {
    return {
      loading: false,
      searchQuery: "",
      messages: { text: "", type: "" },
      responses: [],
      responseCount: 0,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      sidebarOpen: false,
      selectedResponse: null,
      selectedResponseDetails: null,
      sidebarLoading: false,
      downloadLoading: false,
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    surveyTitle() {
      return this.$route.query.title || "Survey";
    },
  },
  created() {
    this.fetchResponses(1);
  },
  methods: {
    onPageSizeChange() {
      this.currentPage = 1;
      if (this.$refs.paginationRef) {
        this.$refs.paginationRef.currentPage = 1;
      }
      this.fetchResponses(1);
    },
    clearFilters() {
      this.searchQuery = "";
      this.fetchResponses(1);
    },
    async fetchResponses(page = 1) {
      this.currentPage = page;
      this.loading = true;
      try {
        const surveyId = this.$route.params.id;
        const response = await axios.get(
          `${process.env.VUE_APP_API}/survey/submitted-responses/?survey__id=${surveyId}&Key=${this.userInfo.token_key}&search=${this.searchQuery || ''}&page=${page}&page_size=${this.pageSize}`,
        );

        if (response.data?.status === 200 || response.status === 200) {
          let list = [];
          if (response.data?.data?.data) {
            list = response.data.data.data;
            this.totalPages =
              response.data.total_page || response.data.data.total_page || 1;
            this.responseCount =
              response.data.total || response.data.data.total || list.length;
          } else {
            list = response.data?.data || [];
            this.totalPages = response.data?.total_page || 1;
            this.responseCount = response.data?.total || list.length;
          }

          this.responses = Array.isArray(list) ? list : [];
        } else {
          this.messages = {
            text: response.data?.message || "Failed to load responses.",
            type: "error",
          };
        }
      } catch (error) {
        console.error("Error fetching responses:", error);
        this.messages = {
          text: error.response?.data?.message || "Error loading responses.",
          type: "error",
        };
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    async openSidebar(resp) {
      this.selectedResponse = resp;
      this.sidebarOpen = true;
      document.body.style.overflow = "hidden";
      this.sidebarLoading = true;
      this.selectedResponseDetails = null;

      try {
        const surveyId = this.$route.params.id;
        const response = await axios.get(
          `${process.env.VUE_APP_API}/survey-user/${resp.user_id}/${surveyId}/?Key=${this.userInfo.token_key}`,
        );
        if (response.data?.status === 200 || response.status === 200) {
          const apiData = response.data?.data;
          this.selectedResponseDetails = Array.isArray(apiData)
            ? apiData[0]
            : apiData;
        } else {
          console.error("API error status:", response.data?.message);
        }
      } catch (error) {
        console.error("Error fetching detailed response:", error);
      } finally {
        this.sidebarLoading = false;
      }
    },
    closeSidebar() {
      this.sidebarOpen = false;
      this.selectedResponse = null;
      this.selectedResponseDetails = null;
      this.sidebarLoading = false;
      document.body.style.overflow = "";
    },
  
    async downloadResponseData() {
       if (!this.selectedResponse) return;
      this.downloadLoading = true;
      try {
        const surveyId = Number(this.$route.params.id);
        const response = await axios.post(
          `${process.env.VUE_APP_API}/survey/response/excel/?Key=${this.userInfo.token_key}`,
          {
            user_id: this.selectedResponse.user_id,
            survey_id: surveyId,
          },
        );

        if (response.data.status === 200) {
          this.messages = {
            text: "Export Successful",
            type: "success",
          };
          setTimeout(() => {
            this.$router.push({ name: "DownloadHistory" });
          }, 500);
        } else {
          this.messages = {
            text: "Event Failed",
            type: "error",
          };
          console.log("not successful");
        }
      } catch (error) {
        console.error("Delete error:", error);
        this.messages = {
          text: "Something went wrong. Please try again.",
          type: "error",
        };
      } finally {
        this.downloadLoading = false;
        setTimeout(async () => {
          this.messages = { text: "", type: "" };
        }, 2500);
      }
    },
  },
};
</script>

<style scoped>
* {
  transition: all 0.2s ease;
}

/* Custom Scrollbar for better UX */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
